import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import _ from 'lodash';
import moment from 'moment';
import * as salesCosts from '../../lib/api/salesCosts';
import { useFormik } from 'formik';

import { useSnackbar } from 'notistack';

import Button from '@mui/material/Button';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import Grid from '@mui/material/Grid';

import MenuItem from '@mui/material/MenuItem';

import Checkbox from '@mui/material/Checkbox';

import Box from '@mui/material/Box';
import 'dayjs/locale/ko'; 
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; 
import DateAdapter from '@mui/lab/AdapterDateFns';
import * as dayjs from 'dayjs'



import CloseIcon from '@mui/icons-material/Close';
import FormLabel from '../../components/common/form/FormLabel';
import useComCodes from '../../hooks/useComCode';

import NumberFormatCustom from '../../components/common/form/NumberFormatCustom';

import EmpModalContainer from '../common/form/modal/EmpModalContainer';
import HrDptSelectbox from '../../components/common/form/selectbox/HrDptSelectbox';

const scLargeFilter = {
  blcCd: 'BASE',
  mlcCd: 'SALES_COST',
  useYn: 'Y',
};

const categoryOptions = [
  { val: '개인직접비', label: '개인직접비' },
  { val: '부서공통비', label: '부서공통비' },
  { val: '회사공통비', label: '회사공통비' },
];

const SalesCostNewEditFormDialog = ({
  open,
  isEdit,
  currentCost,
  onClose,
  refreshAndClose,
  companyOptions,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { getFilteredList } = useComCodes();

  const [selectedLarge, setSelectedLarge] = useState(null);
  const [smallOptions, setSmallOptions] = useState([]);

  const largeOptions = _.uniqBy(getFilteredList(scLargeFilter), 'slcCd');

  useEffect(() => {
    if (selectedLarge) {
      const filteredOptions = getFilteredList({
        ...scLargeFilter,
        slcCd: selectedLarge,
      });
      setSmallOptions(filteredOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLarge]);

  const defaultValues = useMemo(
    () => ({
      scUuid: currentCost?.scUuid || '',
      scDt: currentCost?.scDt || new Date(),
      scLarge: currentCost?.scLarge || '',
      scSmall: currentCost?.scSmall || '',
      scWh: currentCost?.scWh || '',
      scItem: currentCost?.scItem || '',
      scTotal: currentCost?.scTotal || 0,
      scPrice: currentCost?.scPrice || 0,
      scTax: currentCost?.scTax || 0,

      isTax: currentCost?.isTax || 'Y',
      isBill: currentCost?.isBill || '',

      companyCd: currentCost?.companyCd || '',
      companyNm: currentCost?.companyNm || '',

      usedUserNm: currentCost?.usedUserNm || '',
      usedUserUuid: currentCost?.usedUserUuid || '',

//      usedDeptNm: currentCost?.usedDeptNm || '', // 부서명 필드 추가
      usedDeptUuid: currentCost?.usedDeptUuid || '', // 부서 식별자 등 필요시 추가

      usedCategory: currentCost?.usedCategory || '개인직접비',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCost],
  );

  const validationSchema = Yup.object({
    scDt: Yup.mixed().required('일자를 선택하세요'),
    scLarge: Yup.string().required('계정과목(대) 선택하세요'),
    scSmall: Yup.string().required('계정과목(소) 선택하세요'),
    scWh: Yup.string().required('사용처를 선택하세요'),
    scItem: Yup.string().required('품목을 선택하세요'),
    // 판매 관리비가 입력되고, 부가세가 체크 되어 있다면
    scPrice:
      Yup.number('숫자만 가능합니다(소수점은 불가)').required(
        '금액을 입력하세요',
      ),
    // .min(0, '최소 0 이상을 입력하세요'),// 마이너스 입력되도록 변경
    scTax: Yup.number('숫자만 가능합니다(소수점은 불가)').when(
      'isTax',
      (isTax, schema) =>
        isTax === 'Y' ? schema.required('부가세를 입력하세요') : schema,
    ),
    scTotal:
      Yup.number('숫자만 가능합니다(소수점 불가)').required(
        '총금액을 입력하세요',
      ),
    //.min(0, '최소 0 이상을 입력하세요'), // 마이너스 입력되도록 변경
    companyCd: Yup.string().required('회사를 선택하세요.'),
    usedCategory: Yup.string().required('사용분류를 선택하세요.'),
    
    // usedUserNm: Yup.string().required('사용자를 선택하세요.'),
    usedUserNm: Yup.string().when('usedCategory', {
      is: '개인직접비',
      then: Yup.string().required('사용자를 선택하세요.'),
      otherwise: Yup.string(),
    }),
    // 부서직접비인 경우 부서 필드는 필수
    usedDeptUuid: Yup.string().when('usedCategory', {
      is: '부서직접비', // 혹은 categoryOptions에 맞게 '부서공통비'로 변경
      then: Yup.string().required('부서를 선택하세요.'),
      otherwise: Yup.string(),
    }),

  });
//  formik.values.usedCategory
  const formik = useFormik({
    initialValues: defaultValues,
    enableReinitialize: true,
    
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        onSave(values);

        formik.resetForm();
        setSubmitting(false);
      }, 1000);
    },
  });

  const onSave = async (values) => {
    const NewSalesCost = {
      scUuid: values?.scUuid,
      scDt:
        moment(values.scDt).format('YYYY-MM-DD') ||
        moment(new Date()).format('YYYY-MM-DD'),
      scLarge: values?.scLarge,
      scSmall: values?.scSmall,
      scWh: values?.scWh,
      scItem: values?.scItem,
      scPrice: Number(values?.scPrice),
      scTax: values?.isTax !== 'Y' ? 0 : values?.scTax,
      scTotal: Number(values?.scTotal),
      isTax: values?.isTax === 'Y' ? 'Y' : 'N',
      isBill: values?.isBill === 'Y' ? 'Y' : 'N',
      companyCd: values?.companyCd,
      usedUserUuid: values?.usedUserUuid,
      usedDeptUuid: values?.usedDeptUuid,
      usedCategory: values?.usedCategory,
    };
    console.info('DATA', JSON.stringify(NewSalesCost, null, 2));

    try {
      if (isEdit && currentCost) {
        await salesCosts.editSalesCost(NewSalesCost);
      } else {
        delete NewSalesCost.scUuid;
        await salesCosts.addSalesCost(NewSalesCost);
      }
      refreshAndClose();
      enqueueSnackbar('판매관리비 계정 저장 성공', {
        variant: 'success',
      });
    } catch (err) {
      let message =
        err.response !== 'undefined' ? err.response.data.message : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (isEdit && currentCost) {
      // console.log(currentCost, 'currentCost');
      formik.resetForm(defaultValues);
      setSelectedLarge(currentCost?.scLarge);
    }
    if (!isEdit) {
      formik.resetForm(defaultValues);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentCost]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const calcPrices = (strPrice) => {
    //부가세 여부 체크 확인
    const isTaxVal = formik.values?.isTax;
    const price = Number(strPrice);

    const taxVal = isTaxVal === 'Y' ? price / 10 : 0;
    const total = price + Number(taxVal);

    formik.setFieldValue('scTax', taxVal);
    formik.setFieldValue('scTotal', total);
  };

  const calcTotal = (strTax) => {
    //부가세 여부 체크 확인
    const scPrice = Number(formik.values?.scPrice);
    const taxPrice = Number(strTax);
    const total = scPrice + taxPrice;

    formik.setFieldValue('scTotal', total);
  };

  // 부가세가 10% 이기 때문에, 합계액에는 원금(100%)과 부가세(10%)가 포함된 금액
  const calcPriceAndTax = (strTotal) => {
    //부가세 여부 체크 확인
    const isTaxVal = formik.values?.isTax;
    const scTotal = Number(strTotal);

    //부가세가 제외된 원금 = 합계액 * 100 / 110
    const scPrice =
      isTaxVal === 'Y' ? Math.round((scTotal * 100) / 110) : scTotal;
    // 부가세 = 합계액 * 10 / 110
    const scTax = isTaxVal === 'Y' ? Math.round((scTotal * 10) / 110) : 0;

    formik.setFieldValue('scPrice', scPrice);
    formik.setFieldValue('scTax', scTax);
  };

  const [openEmp, setOpenEmp] = useState(false);
  const [empTitle, setEmpTitle] = useState('사원찾기');

  const openEmpModal = (type, title) => {
    setEmpTitle(title);
    setOpenEmp(true);
  };

  const handleEmpClose = () => {
    setOpenEmp(false);
  };

  const selectedEmployee = (emp) => {
    formik.setFieldValue(`usedUserNm`, emp.usrNm, true);
    formik.setFieldValue(`usedUserUuid`, emp.usrUuid, false);
  };

  const title = isEdit
    ? `${currentCost?.scSmall} 수정`
    : `판매관리비 계정 추가`;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="md" >
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <DialogContent sx={{ height: '560px' }} dividers>
          <Grid container sx={{ my: 1 }}>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  일자
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko"
                >
                  <DatePicker
                    format="YYYY-MM-DD"
                    mask={'____-__-__'}
                    id="scDt"
                    name="scDt"
                    value={dayjs(formik?.values?.scDt)}
                    onChange={(newValue) => {
                      formik.setFieldValue('scDt', dayjs(newValue));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        required={false}
                        fullWidth
                        error={
                          formik.touched.scDt && Boolean(formik.errors.scDt)
                        }
                        helperText={formik.touched.scDt && formik.errors.scDt}
                        sx={{ width: '180px' }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  계정과목(대)
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  name="scLarge"
                  id="scLarge"
                  fullWidth
                  select
                  size="small"
                  value={formik.values?.scLarge}
                  defaultValue=""
                  helperText={
                    formik.touched['scLarge'] && formik.errors['scLarge']
                  }
                  error={
                    formik.touched['scLarge'] &&
                    Boolean(formik.errors['scLarge'])
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue('scLarge', event.target.value);
                    setSelectedLarge(event.target.value);
                  }}
                >
                  <MenuItem value="">계정과목(대)를 선택하세요</MenuItem>
                  {largeOptions.map((option) => (
                    <MenuItem key={option.slcCd} value={option.slcCd}>
                      {option.slcCd}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  계정과목(소)
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  name="scSmall"
                  id="scSmall"
                  fullWidth
                  select
                  size="small"
                  value={formik.values?.scSmall}
                  defaultValue=""
                  helperText={
                    formik.touched['scSmall'] && formik.errors['scSmall']
                  }
                  error={
                    formik.touched['scSmall'] &&
                    Boolean(formik.errors['scSmall'])
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue('scSmall', event.target.value);
                  }}
                >
                  <MenuItem value="">계정과목(대)를 선택하세요</MenuItem>
                  {smallOptions.map((option) => (
                    <MenuItem key={option.revVal2} value={option.revVal2}>
                      {option.revVal1}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  사용처
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  id="scWh"
                  name="scWh"
                  value={formik.values.scWh}
                  onChange={formik.handleChange}
                  error={formik.touched.scWh && Boolean(formik.errors.scWh)}
                  helperText={formik.touched.scWh && formik.errors.scWh}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  품목
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  id="scItem"
                  name="scItem"
                  value={formik.values.scItem}
                  onChange={formik.handleChange}
                  error={formik.touched.scItem && Boolean(formik.errors.scItem)}
                  helperText={formik.touched.scItem && formik.errors.scItem}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  판매관리비 금액
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  name="scPrice"
                  id="scPrice"
                  value={formik.values?.scPrice}
                  onChange={(e) => {
                    formik.setFieldValue('scPrice', e.target.value);
                    calcPrices(e.target.value);
                  }}
                  error={
                    formik.touched.scPrice && Boolean(formik.errors.scPrice)
                  }
                  helperText={formik.touched.scPrice && formik.errors.scPrice}
                  onBlur={formik.handleBlur}
                  inputProps={{ isNegative: true }}
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>부가세</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  disabled={formik.values?.isTax !== 'Y'}
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  name="scTax"
                  id="scTax"
                  value={formik.values.scTax}
                  onChange={(e) => {
                    formik.setFieldValue('scTax', e.target.value);
                    calcTotal(e.target.value);
                  }}
                  error={formik.touched.scTax && Boolean(formik.errors.scTax)}
                  helperText={formik.touched.scTax && formik.errors.scTax}
                  onBlur={formik.handleBlur}
                  inputProps={{ isNegative: true }}
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  총 금액
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  size="small"
                  required
                  variant="outlined"
                  fullWidth
                  name="scTotal"
                  id="scTotal"
                  value={formik.values?.scTotal}
                  onChange={(e) => {
                    formik.setFieldValue('scTotal', e.target.value);
                    calcPriceAndTax(e.target.value);
                  }}
                  error={
                    formik.touched.scTotal && Boolean(formik.errors.scTotal)
                  }
                  helperText={formik.touched.scTotal && formik.errors.scTotal}
                  onBlur={formik.handleBlur}
                  inputProps={{ isNegative: true }}
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  부가세 적용여부
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <Checkbox
                  name="isTax"
                  id="isTax"
                  checked={formik.values?.isTax === 'Y'}
                  onChange={(event) => {
                    if (event.target.checked) {
                      formik.setFieldValue('isTax', 'Y');
                    } else {
                      formik.setFieldValue('isTax', 'N');
                      formik.setFieldValue('scTax', '0');
                    }
                  }}
                  inputProps={{ 'aria-label': 'isTax' }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  세금계산서 여부
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <Checkbox
                  name="isBill"
                  id="isBill"
                  checked={formik.values?.isBill === 'Y'}
                  onChange={(event) => {
                    if (event.target.checked) {
                      formik.setFieldValue('isBill', 'Y');
                    } else {
                      formik.setFieldValue('isBill', 'N');
                    }
                  }}
                  inputProps={{ 'aria-label': 'isBill' }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  회사명
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  name="companyCd"
                  id="companyCd"
                  fullWidth
                  select
                  size="small"
                  value={formik.values?.companyCd}
                  defaultValue=""
                  helperText={
                    formik.touched['companyCd'] && formik.errors['companyCd']
                  }
                  error={
                    formik.touched['companyCd'] &&
                    Boolean(formik.errors['companyCd'])
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue('companyCd', event.target.value);
                  }}
                >
                  <MenuItem value="">회사를 선택하세요</MenuItem>
                  {companyOptions.map((option) => (
                    <MenuItem key={option.companyCd} value={option.companyCd}>
                      {option.companyNm}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            {formik.values.usedCategory === '개인직접비' && (
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  사용자
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  disabled
                  fullWidth
                  size="small"
                  required
                  id="usedUserNm"
                  name="usedUserNm"
                  value={formik.values.usedUserNm}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.usedUserNm &&
                    Boolean(formik.errors.usedUserNm)
                  }
                  helperText={
                    formik.touched.usedUserNm && formik.errors.usedUserNm
                  }
                />
              </Grid>
              <Grid item xs={4} sx={{ px: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => openEmpModal('', '사원 선택')}
                >
                  사원 선택
                </Button>
              </Grid>
            </Grid>
            )}
            {formik.values.usedCategory === '부서공통비' && (
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  부서
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={4}>

                <HrDptSelectbox
                   formik={formik}
                   name="usedDeptUuid"
                   label="부서"
                   margin="normal"
                   variant="outlined"
                 />

                {/* <TextField
                  disabled
                  fullWidth
                  size="small"
                  required
                  id="usedUserNm"
                  name="usedUserNm"
                  value={formik.values.usedUserNm}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.usedUserNm &&
                    Boolean(formik.errors.usedUserNm)
                  }
                  helperText={
                    formik.touched.usedUserNm && formik.errors.usedUserNm
                  }
                /> */}
              </Grid>
            </Grid>
            )}
            <Grid container sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <FormLabel>
                  사용분류
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ mx: 1, color: 'error.main' }}
                  >
                    (필수)
                  </Typography>
                </FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  name="usedCategory"
                  id="usedCategory"
                  fullWidth
                  select
                  size="small"
                  value={formik.values?.usedCategory}
                  defaultValue=""
                  helperText={
                    formik.touched['usedCategory'] &&
                    formik.errors['usedCategory']
                  }
                  error={
                    formik.touched['usedCategory'] &&
                    Boolean(formik.errors['usedCategory'])
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue('usedCategory', event.target.value);
                  }}
                >
                  <MenuItem value="">사용분류 선택하세요</MenuItem>
                  {categoryOptions &&
                    categoryOptions.map((option) => (
                      <MenuItem key={option.val} value={option.val}>
                        {option.label}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </DialogActions>
      </Box>
      {openEmp && (
        <EmpModalContainer
          title={empTitle}
          multi={false}
          open={openEmp}
          handleClose={handleEmpClose}
          selectedEmployee={selectedEmployee}
        />
      )}
    </Dialog>
  );
};

export default SalesCostNewEditFormDialog;
